import { CollectionPointSearchParams } from '../../../../types/collectionPoints';
import { joinStreetHouseNumber, joinZipCodeCity } from '../../../../utils/address';
import { Address, SimpleAddress } from '../../../../utils/checkoutState';
import { config } from '../../../../utils/config';
import { reverseGeocode } from '../../../../utils/geocode';
import { getAddress } from '../../../../utils/googleMaps';
import { reportErrorToSentry } from '../../../../utils/initializeSentry';
import { pushTrackingEvent } from '../../../../utils/tracking';

const getFormattedAddress = (address: SimpleAddress) =>
  `${joinStreetHouseNumber({
    country: { iso2Code: config.country },
    street: address.street,
    houseNumber: address.houseNumber,
  })} • ${joinZipCodeCity({
    country: { iso2Code: config.country },
    zipCode: address.zipCode,
    city: address.city,
  })}`;

const getMaximumDistance = () => Math.max(...config.collectionPoints.maximumDistanceOptions) * 1000;

export const getInitialSearchParams = (): CollectionPointSearchParams => ({
  streetHouseNumber: '',
  zipCode: '',
  city: '',
  maximumDistance: config.collectionPoints.defaultMaximumDistance * 1000,
  formattedAddress: '',
});

export const getSearchParamsFromAddress = (address: Address): CollectionPointSearchParams => ({
  streetHouseNumber: joinStreetHouseNumber({
    country: { iso2Code: config.country },
    street: address.street,
    houseNumber: address.houseNumber,
  }),
  zipCode: address.zipCode ?? '',
  city: address.city,
  maximumDistance: getMaximumDistance(),
  formattedAddress: getFormattedAddress(address),
});

export const getSearchParamsFromCurrentPosition = async (): Promise<CollectionPointSearchParams | undefined> => {
  try {
    const position = await new Promise<GeolocationPosition>((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject)
    );
    const geocodedAddress = await reverseGeocode(position.coords);
    const { street, houseNumber, zipCode, city } = geocodedAddress;
    if (!street || !houseNumber || !zipCode || !city) {
      return undefined;
    }
    return {
      streetHouseNumber: joinStreetHouseNumber({ country: { iso2Code: config.country }, street, houseNumber }),
      zipCode,
      city,
      maximumDistance: getMaximumDistance(),
      coordinates: position.coords,
      formattedAddress: getFormattedAddress(geocodedAddress as SimpleAddress),
    };
  } catch (e: any) {
    console.error(e);
    reportErrorToSentry(e);
    pushTrackingEvent({
      event: 'error',
      action: 'collection_points_current_location',
      label: `${config.country}|${e.name}|${e.message}`,
      value: null,
    });
    return undefined;
  }
};

export const getSearchParamsFromPlace = (
  place: google.maps.places.PlaceResult
): CollectionPointSearchParams | undefined => {
  if (!place.address_components) {
    return undefined;
  }
  const address = getAddress(place);
  const { street, city, coordinates } = address;
  if (!street || !city) {
    return undefined;
  }
  const houseNumber = address.houseNumber ?? '1';
  const streetHouseNumber = joinStreetHouseNumber({
    country: { iso2Code: config.country },
    street,
    houseNumber,
  });
  const zipCode = address.zipCode ?? '0000';
  return {
    streetHouseNumber,
    zipCode,
    city,
    maximumDistance: getMaximumDistance(),
    coordinates,
    formattedAddress: getFormattedAddress(address as SimpleAddress),
  };
};
